import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../hooks/useReveal';
import { FaWhatsapp, FaShoppingCart } from "react-icons/fa";
import './Home.css';
import { Helmet } from 'react-helmet-async';


// ── Banner images ──
const ban1 = "/images/ban.webp";
const ban2 = "/images/ban2.webp";
const ban3 = "/images/ban3.webp";

import ProductDetail from './ProductDetail';
import Products from './Products';

const agr1 = "/images/agr1.webp";
const agr2 = "/images/agr2.webp";
const agr3 = "/images/agr3.webp";
const agrhomab1 = "/images/homeabou1.webp";

const sagarr = "/images/bhaktisagaragrbatti1.webp";
const goldChandan = "/images/gold-chandan.webp";
const oudh1 = "/images/oudh1.webp";

const mban1 = "/images/mban1.webp";
const mban2 = "/images/mban2.webp";
const mban3 = "/images/mban3.webp";


const DESKTOP_BANNERS = [ban1, ban2, ban3];
const MOBILE_BANNERS  = [mban1, mban2, mban3];


const SLIDE_INTERVAL = 4000;

// Unique warm gradient accent per slide
const SLIDE_COLORS = [
  { from: '#ffffff00', to: '#f7c59f00' },
  { from: '#c03a2b00', to: '#f39d1200' },
  { from: '#27ae5f00', to: '#f9cb2400' },
  { from: '#8d44ad00', to: '#f5cba700' },
  { from: '#1a537600', to: '#48c9af00' },
];

const STATS = [
  { num:"50+", label:"Unique Fragrances", icon:"🌺" },
  { num:"2L+", label:"Happy Devotees", icon:"🙏" },
  { num:"500+", label:"Retail Partners", icon:"🤝" },
  { num:"", label:"Natural Ingredients", icon:"🌿" }
];

const FEATURES = [
  { icon:"🌿", title:"Natural", desc:"Made from Natural Ingredients." },
  { icon:"✋", title:"Made with Mastery", desc:"Each stick carries the skill of artisans trained for over two decades." },
  { icon:"🙏", title:"Vedic Tradition", desc:"Ancient formulas passed down through generations." },
  { icon:"🚚", title:"Pan-India Netwrok", desc:"From Mumbai to every corner of India." },
  { icon:"🌍", title:"Eco-Friendly", desc:"Recyclable Packaging" },
  { icon:"✅", title:"Quality Assured", desc:"Rigorous testing ensures consistent, premium output." }
];

const TESTIMONIALS = [
  { name:"Ritu Sharma", place:"Bhopal", text:"The Chandan Agarbatti fills my puja room with the most divine fragrance. Been buying for 5 years — quality never wavers.", stars:5 },
  { name:"Suresh Patel", place:"Ahmedabad", text:"We use their dhoop sticks in our temple. The congregation always comments on the beautiful aroma. Truly divine.", stars:5 },
  { name:"Meena Iyer", place:"Mumbai", text:"Oud Extra Premium Agarbatti is worth every rupee. The fragrance lingers for hours even after it stops burning!", stars:5 },
  { name:"Rajesh Kumar", place:"Delhi", text:"Bulk ordered for our store. Customers love the Royal Musk Dhoop Cones. Already placing a reorder!", stars:5 }
];

// ════════════════════════════════════════
//   HERO GRADIENT SLIDER
// ════════════════════════════════════════
function HeroSlider() {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [dir, setDir] = useState("next");
  const [phase, setPhase] = useState("idle");
  const [progress, setProgress] = useState(0);

  const timerRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(null);

  const slides = isMobile
    ? [mban1, mban2, mban3]
    : [ban1, ban2, ban3];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const goTo = useCallback(
    (idx, direction = "next") => {
      if (phase === "slide-in" || idx === current) return;

      clearInterval(timerRef.current);
      setDir(direction);
      setPrev(current);
      setCurrent(idx);
      setPhase("slide-in");
      setProgress(0);

      setTimeout(() => {
        setPrev(null);
        setPhase("idle");
      }, 950);
    },
    [phase, current]
  );

  const next = useCallback(() => {
    goTo((current + 1) % slides.length, "next");
  }, [current, goTo, slides.length]);

  const prevSlide = useCallback(() => {
    goTo((current - 1 + slides.length) % slides.length, "prev");
  }, [current, goTo, slides.length]);

  useEffect(() => {
    timerRef.current = setInterval(next, SLIDE_INTERVAL);
    return () => clearInterval(timerRef.current);
  }, [next]);

  useEffect(() => {
    setProgress(0);
    startRef.current = null;

    const tick = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min(((ts - startRef.current) / SLIDE_INTERVAL) * 100, 100);
      setProgress(p);

      if (p < 100) {
        rafRef.current = requestAnimationFrame(tick);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [current]);

  const c = SLIDE_COLORS[current];
  const pColor = prev !== null ? SLIDE_COLORS[prev] : null;

  return (
    <section className="hero" aria-label="Hero Slider">
      <div
        className="hero__grad-bg"
        style={{
          background: `linear-gradient(135deg, ${c.from}22 0%, ${c.to}33 100%)`,
        }}
      />

      <div className="hero__track">
        {prev !== null && (
          <div
            className={`hero__slide hero__slide--exit hero__slide--exit-${dir}`}
            style={{ backgroundImage: `url(${slides[prev]})` }}
          >
            <div
              className="hero__gloss"
              style={{
                background: `linear-gradient(135deg, ${pColor.from}55 0%, transparent 55%, ${pColor.to}33 100%)`,
              }}
            />
          </div>
        )}

        <div
          className={`hero__slide hero__slide--enter hero__slide--enter-${dir} ${
            phase === "slide-in"
              ? "hero__slide--animating"
              : "hero__slide--resting"
          }`}
          style={{ backgroundImage: `url(${slides[current]})` }}
        >
          <div
            className="hero__gloss"
            style={{
              background: `linear-gradient(135deg, ${c.from}55 0%, transparent 55%, ${c.to}33 100%)`,
            }}
          />
          <div className="hero__bottom-fade" />
        </div>
      </div>

      <div className="hero__orbs" aria-hidden="true">
        <div
          className="hero__orb hero__orb--1"
          style={{
            background: `radial-gradient(circle, ${c.from}66, transparent 70%)`,
          }}
        />
        <div
          className="hero__orb hero__orb--2"
          style={{
            background: `radial-gradient(circle, ${c.to}55, transparent 70%)`,
          }}
        />
      </div>

      <div className="hero__grain" aria-hidden="true" />

      <button
        className="hero__arrow hero__arrow--left"
        onClick={prevSlide}
        aria-label="Previous slide"
        style={{ "--accent": c.from }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="15 18 9 12 15 6" />
        </svg>
      </button>

      <button
        className="hero__arrow hero__arrow--right"
        onClick={next}
        aria-label="Next slide"
        style={{ "--accent": c.from }}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>

      <div className="hero__dots" role="tablist">
        {slides.map((_, i) => (
          <button
            key={i}
            role="tab"
            aria-selected={i === current}
            aria-label={`Slide ${i + 1}`}
            className={`hero__dot ${i === current ? "hero__dot--active" : ""}`}
            onClick={() => goTo(i, i > current ? "next" : "prev")}
          >
            <span
              className="hero__dot-fill"
              style={
                i === current
                  ? {
                      background: `linear-gradient(90deg, ${c.from}, ${c.to})`,
                      width: `${progress}%`,
                    }
                  : i < current
                  ? {
                      width: "100%",
                      background: `linear-gradient(90deg, ${SLIDE_COLORS[i].from}, ${SLIDE_COLORS[i].to})`,
                    }
                  : { width: "0%" }
              }
            />
          </button>
        ))}
      </div>

      <div className="hero__counter" aria-hidden="true">
        <span className="hero__counter-current" style={{ color: c.from }}>
          {String(current + 1).padStart(2, "0")}
        </span>
        <span className="hero__counter-sep" />
        <span className="hero__counter-total">
          {String(slides.length).padStart(2, "0")}
        </span>
      </div>

      <div
        className="hero__accent-bar"
        style={{
          background: `linear-gradient(90deg, ${c.from}, ${c.to}, ${c.from})`,
        }}
      />
    </section>
  );
}

// ════════════════════════════════════════
//   HOME PAGE
// ════════════════════════════════════════
export default function Home() {
  useReveal();

  return (

    <>
    
    <main>
      <Helmet>
  <title>Prakhyat Agarbatti | Premium Masala Incense Sticks & Dhoop — Mumbai</title>
  <meta
    name="description"
    content="Buy premium Prakhyat Agarbatti — handcrafted Masala Agarbatti, Dhoop Cones & Dhoop Sticks made from natural ingredients. Sandalwood, Oud, Lavender & 50+ fragrances. Trusted by 2 lakh+ devotees across India."
  />
  <meta
    name="keywords"
    content="agarbatti, incense sticks, masala agarbatti, dhoop cone, dhoop stick, sandalwood agarbatti, oud agarbatti, premium agarbatti, natural incense, pooja agarbatti, prakhyat agarbatti, buy agarbatti online, agarbatti mumbai, agarbatti india, chandan agarbatti, guggal dhoop, meditation incense"
  />
  <meta name="robots" content="index, follow" />
  <link rel="canonical" href="https://prakhyatagarbatti.com/" />
</Helmet>
      {/* ===================== HERO ===================== */}
      <HeroSlider />

      {/* ===================== STATS ===================== */}
      <section className="home-stats" aria-label="Company Statistics">
        <div className="home-stats__track">
          {[...STATS, ...STATS].map((s, i) => (
            <div key={i} className="home-stats__item">
              <span className="home-stats__icon">{s.icon}</span>
              <strong className="home-stats__num">{s.num}</strong>
              <span className="home-stats__label">{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ===================== BRAND INTRO ===================== */}
      <section className="section home-intro" aria-labelledby="intro-heading">
        <div className="container">
          <div className="home-intro__grid">
            <div className="home-intro__visual reveal-l">
              <div className="home-intro__img-wrap">
<img 
  src={agrhomab1} 
  alt="Prakhyat Agarbatti — handcrafted incense from Virar Palghar" 
  loading="lazy" 
/>                
              </div>
              <div className="home-intro__visual-card reveal-s" style={{transitionDelay:'0.3s'}}>
                <div className="vc-icon">🌿</div>
                <strong>100% Natural</strong>
                <p>Pure herbs &amp; resins only</p>
              </div>
            </div>
            <div className="home-intro__text reveal-r">
              <div className="eyebrow">Our Heritage</div>
              <h2 className="h-display" id="intro-heading">A Premium Brand Rooted in Fragrance, Faith and Tradition</h2>
              <div className="divider-ornament"><span/><span>🪔</span><span/></div>
            <p className="h-sub">
                <strong>PRAKHYAT Agarbattis</strong> is a premium and highly
                appreciated brand among consumers, operating under the flagship
                of <strong>Pushpak Aroma Products</strong>, makers of finest
                quality traditional Indian Fluxo / Masala Agarbattis and
                perfumed incense sticks since many years.
              </p>

              <p className="h-sub" style={{ marginTop: "1rem" }}>
                In the modern era, agarbattis and incense sticks are used not
                only for prayers across religions, but also for aromatherapy,
                yoga, meditation, Vaastu Shanti, and for purifying and cleansing
                the atmosphere. Pleasant fragrance is known to relieve stress,
                reduce tension, and bring peace to the mind, body and soul.
              </p>
              <div style={{display:'flex', gap:'1rem', flexWrap:'wrap', marginTop:'2rem'}}>
                <Link to="/about" className="btn btn-primary">✦ Our Story</Link>
                <Link to="/products" className="btn btn-green">View Products</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===================== PRODUCT CATEGORIES ===================== */}
      <section className="section home-cats" style={{background:'var(--light-bg)'}} aria-labelledby="cats-heading">
        <div className="container">
          <div className="text-center reveal">
            <div className="eyebrow" style={{justifyContent:'center'}}>Our Collections</div>
            <h2 className="h-display" id="cats-heading">Three Sacred Categories</h2>
            <p className="h-sub" style={{margin:'0.75rem auto 0'}}>Standard · Premium · Extra Premium — each tier crafted for different budgets and occasions</p>
          </div>
          <div className="home-cats__grid">
            {[
             { 
    type: "Agarbatti",
    // icon: "🌸",
    link: "/products/agarbatti",
    img: agr1,
    desc: "Incense Sticks",
    variants: "Rose · Sandalwood · Oud",
    color: "var(--green)"
  },
  { 
    type: "Dhoop Cone",
    // icon: "🔺",
    link: "/products/dhoop-cone",
    img: agr2,
    desc: "Sacred Cones",
    variants: "Lavender · Rose · Royal Musk",
    color: "var(--navy)"
  },
  { 
    type: "Dhoop Stick",
    // icon: "🪵",
    link: "/products/dhoop-stick",
    img: agr3,
    desc: "Vedic Sticks",
    variants: "Guggal · Kewra · Bakhoor",
    color: "var(--red)"
  }
            ].map((cat,i) => (
              <Link key={i} to={cat.link} className="home-cat-card reveal" style={{transitionDelay:`${i*0.15}s`}} aria-label={`${cat.type} collection`}>
                <div className="hcc-img">
                  <img src={cat.img} alt={`${cat.type} by Prakhyat Agarbatti`} loading="lazy" />
                  <div className="hcc-overlay" />
                  <div className="hcc-icon">{cat.icon}</div>
                </div>
                <div className="hcc-body">
                  <p className="hcc-sub">{cat.desc}</p>
                  <h3 className="hcc-name">{cat.type}</h3>
                  <p className="hcc-variants">{cat.variants}</p>
                  <div className="hcc-cta" style={{color:cat.color}}>Explore Collection →</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== FEATURED PRODUCTS ===================== */}
      <section className="section home-featured" aria-labelledby="featured-heading">
        <div className="container">
          <div className="text-center reveal">
            <div className="eyebrow" style={{justifyContent:'center'}}>Bestsellers</div>
            <h2 className="h-display" id="featured-heading">Most Loved Products</h2>
          </div>
          <div className="home-featured__grid">
            {[



             
             {
  id: "bhakti-sagar-agarbatti",
  img: sagarr,
  name: "Bhakti Sagar Agarbatti",
  tag: "Premium Masala · Fancy Flora Floral",
  price: "₹85",
  type: "Agarbatti"
},
             
{
  id:"gold-chandan-masala-cone-dhoop",

img: goldChandan,
  name:"Prakhyat Gold Chandan Masala Cone Dhoop",
  tag: "Extra Premium · Temple Floral",
  price: "₹85",
  type: "Dhoop Cone"
},
{
  id: "oudh-dhoop-stick",
  img: oudh1,
  name: "Prakhyat Oudh Dhoop Stick",
  tag: "Extra Premium · Royal Oudh",
  price: "₹120",
  type: "Dhoop Stick"
}


            ].map((p,i) => (
              <div key={i} className="fp-card reveal" style={{transitionDelay:`${i*0.12}s`}}>
                <Link to={`/products/${p.id}`}>
                  <div className="fp-img">
                    <img src={p.img} alt={p.name} loading="lazy" />
                    <div className="fp-badge">{p.type}</div>
                  </div>
                  <div className="fp-body">
                    <p className="fp-tag">{p.tag}</p>
                    <h3 className="fp-name">{p.name}</h3>
                  </div>
                </Link>
               <div className="fp-actions">

  <a 
    href={p.flipkartUrl}
    target="_blank"
    rel="noreferrer"
    className="btn btn-flipkart"
  >
    🛒 Buy on Flipkart
  </a>
 <a 
    href={`https://wa.me/919320905751?text=${encodeURIComponent(
      `Hi, I want to enquire about ${p.name}`
    )}`}
    className="btn btn-wa"
    target="_blank"
    rel="noreferrer"
  >
    <FaWhatsapp className="btn-icon" />
    Enquiry
  </a>

</div>
              </div>
            ))}
          </div>
          <div className="text-center" style={{marginTop:'3rem'}}>
            <Link to="products/agarbatti" className="btn btn-primary">✦ View All Products</Link>
          </div>
        </div>
      </section>

      {/* ===================== FRAGRANCE MARQUEE ===================== */}
      <div className="frag-marquee" aria-hidden="true">
       <div className="marquee-track">
  {[
    "🪵 Dev Chandan",
    "🌸 Bhakti Sagar",
    "🕉️ Satsang",
    "🙏 Sai Shraddha",
    "🍓 Hare Ram",
    "🌼 White Flower",
    "✨ Namaskar",
    "🔥 Vighnaharta",
    "🌺 Panchratna",
    "🌙 Dwarkadhish",
    "🧘 Swami Samarth",
    "🌹 Narayani Flora",
    "💫 Hari Krupa",
    "🍃 Hare Krishna",
    "🎉 Swagat",
    "💛 Ashish",
    "🌸 Anmol Sugandh",
    "🌷 Anmol Flora",
    "🌿 Anmol Nagchampa",
    "☁️ Sai Heaven",
    "🪔 Anmol Chandan",
    "🛕 Shri Satya Narayan",
    "🐘 Shri Satya Vinayak",
    "🌱 Govardhan",
    "✨ Aadishakti Flora",
    "🔥 Anmol Oudh",
    "🦌 Kashmiri Kastoori",
    "🧘 Sadhguru",
    "🌲 Richwoods",
    "🌼 Shristhi",
    "🛕 Sanatan",
    "💛 Kesar Chandan",
    "🌈 Paradise",
    "☁️ Seven Heaven",
    "🪵 Vibhuthi",
    "🌸 Vrindavan",
    "🌳 Chandan Van",
    "🫐 Black N Berry",
    "👑 Royal Sweden",
    "🌹 Garden Rose"
  ].map((f, i) => (
    <span key={i} className="frag-chip">{f}</span>
  ))}
</div>
      </div>

      {/* ===================== WHY CHOOSE US ===================== */}
      <section className="section home-why" aria-labelledby="why-heading">
        <div className="home-why__bg" />
        <div className="container home-why__inner">
          <div className="text-center reveal">
            <div className="eyebrow" style={{justifyContent:'center', color:'var(--yellow)'}}>Why Prakhyat</div>
            <h2 className="h-display on-dark" id="why-heading">Crafted with Love, Blessed by Nature</h2>
          </div>
          <div className="home-why__grid">
            {FEATURES.map((f,i) => (
              <div key={i} className="why-card reveal" style={{transitionDelay:`${i*0.1}s`}}>
                <div className="why-card__icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== TESTIMONIALS ===================== */}
      <section className="section home-t  esti" aria-labelledby="testi-heading">
        <div className="container">
          <div className="text-center reveal">
            <div className="eyebrow" style={{justifyContent:'center'}}>Reviews</div>
            <h2 className="h-display" id="testi-heading">What Devotees Say</h2>
          </div>
          <div className="testi-grid">
            {TESTIMONIALS.map((t,i) => (
              <article key={i} className="testi-card reveal" style={{transitionDelay:`${i*0.12}s`}}>
                <div className="testi-stars">{"★".repeat(t.stars)}</div>
                <blockquote className="testi-text">"{t.text}"</blockquote>
                <div className="testi-author">
                  <div className="testi-av">{t.name[0]}</div>
                  <div>
                    <div className="testi-name">{t.name}</div>
                    <div className="testi-place">📍 {t.place}</div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ===================== BULK BANNER ===================== */}
      <section className="home-bulk" aria-label="Bulk Orders">
        <div className="home-bulk__bg" />
        <div className="container home-bulk__inner reveal">
          <div className="home-bulk__text">
            <div className="eyebrow" style={{color:'var(--yellow-warm)'}}>For Businesses &amp; Temples</div>
            <h2 style={{fontFamily:'var(--font-display)', fontSize:'2rem', color:'#fff'}}>Need Customized or Bulk supply Agarbattis</h2>
            <p style={{fontFamily:'var(--font-body)', opacity:0.8, marginTop:'0.5rem'}}>We supply customized products as per choice for Exports, Institutions, Temples, Corporates, etc on a Volume base pricing</p>
          </div>
          <div style={{display:'flex', gap:'1rem', flexShrink:0, flexWrap:'wrap'}}>
            <Link to="/contact" className="btn btn-outline-white">🤝 Become Distributor</Link>
          </div>
        </div>
      </section>

      {/* ===================== CONTACT STRIP ===================== */}
      <section className="home-contact-strip section-sm" aria-label="Contact Information">
        <div className="container">
          <div className="hcs-grid">
            {[
              { icon:"📞", label:"Call Us", value:"+91 93209 05751", href:"tel:+919320905751" },
              { icon:"💬", label:"WhatsApp", value:"Chat Now", href:"https://wa.me/919320905751" },
              { icon:"📍", label:"Find Us", value:"Mumbai , Maharashtra", href:"/contact" },
              { icon:"✉️", label:"Email", value:"info@prakhyatagarbatti.com", href:"mailto:info@prakhyatagarbatti.com" }
            ].map((c,i) => (
              <a key={i} href={c.href} className="hcs-card reveal" style={{transitionDelay:`${i*0.1}s`}}
                target={c.href.startsWith('http') ? '_blank' : undefined}
                rel={c.href.startsWith('http') ? 'noreferrer' : undefined}>
                <span className="hcs-icon">{c.icon}</span>
                <div>
                  <div className="hcs-label">{c.label}</div>
                  <div className="hcs-value">{c.value}</div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>


    </main>
    </>
  );
}
